// Common verbs for the Grammar screen, written once and shared by all five languages.
// Each row is one meaning. The word in each language gives that language's verb list,
// and the same row gives the meaning shown in the user's interface language.
// A word of null means that language has no entry for the row. gloss overrides the meaning shown in one interface language.

const CODE = { english: 'en', italian: 'it', spanish: 'es', french: 'fr', portuguese: 'pt' };

const VERB_CONCEPTS = [
    { en: 'go', it: 'andare', es: 'ir', fr: 'aller', pt: 'ir' },
    { en: 'come', it: 'venire', es: 'venir', fr: 'venir', pt: 'vir' },
    { en: 'be', it: 'essere', es: 'ser', fr: 'être', pt: 'ser' },
    { en: null, it: 'stare', es: 'estar', fr: null, pt: 'estar', gloss: { en: 'to be (state / location)' } },
    { en: 'have', it: 'avere', es: 'tener', fr: 'avoir', pt: 'ter' },
    { en: 'do', it: 'fare', es: 'hacer', fr: 'faire', pt: 'fazer', gloss: { en: 'to do / make' } },
    { en: 'make', it: 'creare', es: 'crear', fr: 'créer', pt: 'criar' },
    { en: 'say', it: 'dire', es: 'decir', fr: 'dire', pt: 'dizer' },
    { en: 'tell', it: 'raccontare', es: 'contar', fr: 'raconter', pt: 'contar' },
    { en: 'get', it: 'ottenere', es: 'conseguir', fr: 'obtenir', pt: 'conseguir' },
    { en: 'give', it: 'dare', es: 'dar', fr: 'donner', pt: 'dar' },
    { en: 'take', it: 'prendere', es: 'tomar', fr: 'prendre', pt: 'pegar' },
    { en: 'see', it: 'vedere', es: 'ver', fr: 'voir', pt: 'ver' },
    { en: 'look', it: 'guardare', es: 'mirar', fr: 'regarder', pt: 'olhar', gloss: { en: 'to look / watch' } },
    { en: 'know', it: 'sapere', es: 'saber', fr: 'savoir', pt: 'saber', gloss: { en: 'to know (a fact)' } },
    { en: null, it: 'conoscere', es: 'conocer', fr: 'connaître', pt: 'conhecer', gloss: { en: 'to know (someone)' } },
    { en: 'think', it: 'pensare', es: 'pensar', fr: 'penser', pt: 'pensar' },
    { en: 'believe', it: 'credere', es: 'creer', fr: 'croire', pt: 'acreditar' },
    { en: 'want', it: 'volere', es: 'querer', fr: 'vouloir', pt: 'querer' },
    { en: 'need', it: null, es: 'necesitar', fr: 'avoir besoin de', pt: 'precisar', gloss: { it: 'avere bisogno di' } },
    { en: 'like', it: 'piacere', es: 'gustar', fr: 'aimer', pt: 'gostar', gloss: { en: 'to like', it: 'piacere (essere gradito)' } },
    { en: 'love', it: 'amare', es: 'amar', fr: null, pt: 'amar' },
    { en: 'work', it: 'lavorare', es: 'trabajar', fr: 'travailler', pt: 'trabalhar' },
    { en: 'study', it: 'studiare', es: 'estudiar', fr: 'étudier', pt: 'estudar' },
    { en: 'learn', it: 'imparare', es: 'aprender', fr: 'apprendre', pt: 'aprender' },
    { en: 'teach', it: 'insegnare', es: 'enseñar', fr: 'enseigner', pt: 'ensinar' },
    { en: 'ask', it: 'chiedere', es: 'preguntar', fr: 'demander', pt: 'perguntar' },
    { en: 'answer', it: 'rispondere', es: 'responder', fr: 'répondre', pt: 'responder' },
    { en: 'speak', it: 'parlare', es: 'hablar', fr: 'parler', pt: 'falar', gloss: { en: 'to speak / talk' } },
    { en: 'listen', it: 'ascoltare', es: 'escuchar', fr: 'écouter', pt: 'escutar' },
    { en: 'hear', it: 'sentire', es: 'oír', fr: 'entendre', pt: 'ouvir', gloss: { en: 'to hear' } },
    { en: 'read', it: 'leggere', es: 'leer', fr: 'lire', pt: 'ler' },
    { en: 'write', it: 'scrivere', es: 'escribir', fr: 'écrire', pt: 'escrever' },
    { en: 'buy', it: 'comprare', es: 'comprar', fr: 'acheter', pt: 'comprar' },
    { en: 'sell', it: 'vendere', es: 'vender', fr: 'vendre', pt: 'vender' },
    { en: 'pay', it: 'pagare', es: 'pagar', fr: 'payer', pt: 'pagar' },
    { en: 'find', it: 'trovare', es: 'encontrar', fr: 'trouver', pt: 'encontrar' },
    { en: 'lose', it: 'perdere', es: 'perder', fr: 'perdre', pt: 'perder' },
    { en: 'begin', it: 'cominciare', es: 'empezar', fr: 'commencer', pt: 'começar' },
    { en: 'start', it: 'iniziare', es: 'iniciar', fr: 'démarrer', pt: 'iniciar' },
    { en: 'finish', it: 'finire', es: 'terminar', fr: 'finir', pt: 'terminar' },
    { en: 'stop', it: 'fermare', es: 'parar', fr: 'arrêter', pt: 'parar', gloss: { en: 'to stop' } },
    { en: 'continue', it: 'continuare', es: 'continuar', fr: 'continuer', pt: 'continuar' },
    { en: 'wait', it: 'aspettare', es: 'esperar', fr: 'attendre', pt: 'esperar' },
    { en: 'stay', it: 'restare', es: 'quedar', fr: 'rester', pt: 'ficar' },
    { en: 'remain', it: 'rimanere', es: 'permanecer', fr: 'demeurer', pt: 'permanecer' },
    { en: 'leave', it: 'partire', es: 'partir', fr: 'partir', pt: 'partir', gloss: { en: 'to leave / depart' } },
    { en: null, it: 'uscire', es: 'salir', fr: 'sortir', pt: 'sair', gloss: { en: 'to go out' } },
    { en: 'arrive', it: 'arrivare', es: 'llegar', fr: 'arriver', pt: 'chegar' },
    { en: 'return', it: 'tornare', es: 'volver', fr: 'retourner', pt: 'voltar' },
    { en: 'walk', it: 'camminare', es: 'caminar', fr: 'marcher', pt: 'caminhar' },
    { en: 'run', it: 'correre', es: 'correr', fr: 'courir', pt: 'correr' },
    { en: 'drive', it: 'guidare', es: 'conducir', fr: 'conduire', pt: 'dirigir' },
    { en: 'fly', it: 'volare', es: 'volar', fr: 'voler', pt: 'voar' },
    { en: 'eat', it: 'mangiare', es: 'comer', fr: 'manger', pt: 'comer' },
    { en: 'drink', it: 'bere', es: 'beber', fr: 'boire', pt: 'beber' },
    { en: 'sleep', it: 'dormire', es: 'dormir', fr: 'dormir', pt: 'dormir' },
    { en: 'wake', it: 'svegliarsi', es: 'despertar', fr: 'se réveiller', pt: 'acordar', gloss: { en: 'to wake up' } },
    { en: 'live', it: 'vivere', es: 'vivir', fr: 'vivre', pt: 'viver' },
    { en: 'die', it: 'morire', es: 'morir', fr: 'mourir', pt: 'morrer' },
    { en: null, it: 'nascere', es: 'nacer', fr: 'naître', pt: 'nascer', gloss: { en: 'to be born' } },
    { en: 'meet', it: 'incontrare', es: null, fr: 'rencontrer', pt: null },
    { en: 'call', it: 'chiamare', es: 'llamar', fr: 'appeler', pt: 'chamar' },
    { en: 'open', it: 'aprire', es: 'abrir', fr: 'ouvrir', pt: 'abrir' },
    { en: 'close', it: 'chiudere', es: 'cerrar', fr: 'fermer', pt: 'fechar' },
    { en: 'put', it: 'mettere', es: 'poner', fr: 'mettre', pt: 'colocar' },
    { en: 'forget', it: 'dimenticare', es: 'olvidar', fr: 'oublier', pt: 'esquecer' },
    { en: 'remember', it: 'ricordare', es: 'recordar', fr: 'se souvenir', pt: 'lembrar' },
    { en: 'decide', it: 'decidere', es: 'decidir', fr: 'décider', pt: 'decidir' },
    { en: 'understand', it: 'capire', es: 'entender', fr: 'comprendre', pt: 'entender' },
    { en: null, it: 'potere', es: 'poder', fr: 'pouvoir', pt: 'poder', gloss: { en: 'to be able to / can' } },
    { en: null, it: 'dovere', es: 'deber', fr: 'devoir', pt: 'dever', gloss: { en: 'to have to / must' } },
    { en: 'enter', it: 'entrare', es: 'entrar', fr: 'entrer', pt: 'entrar' },
    { en: 'play', it: 'giocare', es: 'jugar', fr: 'jouer', pt: 'jogar' },
    { en: 'explain', it: 'spiegare', es: 'explicar', fr: 'expliquer', pt: 'explicar' },
    { en: 'seem', it: 'sembrare', es: 'parecer', fr: 'sembler', pt: 'parecer' },
    { en: 'hold', it: 'tenere', es: 'sujetar', fr: 'tenir', pt: 'segurar', gloss: { en: 'to hold / keep' } },
    { en: null, it: 'scendere', es: 'bajar', fr: 'descendre', pt: 'descer', gloss: { en: 'to go down' } },
    { en: null, it: 'cercare', es: 'buscar', fr: 'chercher', pt: 'buscar', gloss: { en: 'to look for' } },
];

// The verb list for one learning language, e.g. getCommonVerbs('spanish')
export function getCommonVerbs(language) {
    const code = CODE[language] || 'it';
    const seen = new Set();
    const list = [];
    VERB_CONCEPTS.forEach(row => {
        const word = row[code];
        if (!word || seen.has(word)) return;
        seen.add(word);
        list.push({ word, translation: '', row });
    });
    return list;
}

// The meaning of a verb shown in the interface language (empty when the two languages are the same)
export function verbMeaning(row, interfaceLanguage, language) {
    const target = CODE[language] || 'it';
    if (interfaceLanguage === target) return '';
    const gloss = row.gloss && row.gloss[interfaceLanguage];
    if (gloss) return gloss;
    const word = row[interfaceLanguage];
    if (!word) return '';
    return interfaceLanguage === 'en' ? `to ${word}` : word;
}